import type { AntCaste, AntSimWorld, AntState, CasteRatios, ColonySnapshot, NestConfig } from './contracts';

export interface Brood {
  hatchAt: number;
  caste: AntCaste;
  phase: 'egg' | 'larva';
  phaseFlipAt: number;
  /** True if this egg was laid while the colony was out of food (queen
   *  paid HP). Hungry brood with no nurses on the roster has a 50%
   *  chance of failing at hatch time. */
  hungry: boolean;
}

export interface ColonyOptions {
  eggCost?: number;
  eggInterval?: number;
  hatchDuration?: number;
  ratios?: Partial<CasteRatios>;
  /** Hard cap on alive same-team ants; eggs stop laying when reached. */
  maxPopulation?: number;
}

const DEFAULT_RATIOS: CasteRatios = {
  worker:  0.55,
  soldier: 0.15,
  nurse:   0.12,
  scout:   0.10,
  breeder: 0.08,
};

/**
 * Per-team colony tracker. Owns food stockpile, egg / larva lifecycle, queen
 * reference, and caste policy across worker/soldier/nurse/scout/breeder.
 */
export class Colony {
  stockpile = 0;
  private _brood: Brood[] = [];
  queenId = -1;
  alive = true;
  private nextEggAt = 0;

  get brood(): ReadonlyArray<Brood> {
    return this._brood;
  }

  readonly eggCost: number;
  readonly eggInterval: number;
  readonly hatchDuration: number;
  readonly maxPopulation: number;
  ratios: CasteRatios;

  constructor(
    readonly team: 0 | 1,
    readonly nest: NestConfig,
    opts: ColonyOptions = {},
  ) {
    this.eggCost = opts.eggCost ?? 4;
    this.eggInterval = opts.eggInterval ?? 6;
    this.hatchDuration = opts.hatchDuration ?? 20;
    this.maxPopulation = opts.maxPopulation ?? 300;
    this.ratios = { ...DEFAULT_RATIOS, ...(opts.ratios ?? {}) };
  }

  addFood(amount: number): void {
    this.stockpile += amount;
  }

  setQueen(id: number): void {
    this.queenId = id;
  }

  onQueenDeath(): void {
    this.alive = false;
  }

  /** Bulk-set caste ratios. Values will be normalised by the caller; we don't
   *  enforce sum=1 here so live slider drags don't fight each other. */
  setRatios(r: Partial<CasteRatios>): void {
    this.ratios = { ...this.ratios, ...r };
  }

  update(dt: number, elapsed: number, world: AntSimWorld & { spawn(x: number, z: number, team: 0 | 1, caste?: AntCaste): number }): void {
    // Caste-driven ambient food economy + consumption. Only ants that are
    // ABOVE ground can actually forage — anyone with nestCycleT > 0 is
    // hidden underground and can't bring food in. The whole colony (including
    // brood and queen) still eats while hiding, so the stockpile dwindles.
    let workerAbove = 0, scoutAbove = 0, soldierAbove = 0, nurseAmbient = 0, aliveTotal = 0;
    for (const a of world.ants) {
      if (!a.alive || a.team !== this.team) continue;
      aliveTotal++;
      if (a.casteType === 'nurse') nurseAmbient++;
      if (a.nestCycleT > 0) continue;
      if (a.casteType === 'worker') workerAbove++;
      else if (a.casteType === 'scout') scoutAbove++;
      else if (a.casteType === 'soldier') soldierAbove++;
    }
    const productionRate =
      workerAbove * 0.05 + Math.min(workerAbove, scoutAbove) * 0.05 - soldierAbove * 0.008;
    // Every alive ant + brood item consumes a slow trickle. With 100 ants +
    // 20 brood at ~0.003/s, that's ~0.36/s — comparable to the production
    // from 8 working workers. Plenty of headroom unless you're hiding.
    const consumptionRate = (aliveTotal + this._brood.length) * 0.003;
    const net = productionRate - consumptionRate;
    this.stockpile = Math.max(0, this.stockpile + net * dt);

    // Hatch brood whose timer is up; promote eggs to larvae at the mid-point.
    // Hungry brood (laid when stockpile was empty) has a 50 % chance of
    // dying in the egg if there are no nurses to tend it. Healthy brood
    // always hatches; hungry brood with at least one nurse always hatches.
    for (let i = this._brood.length - 1; i >= 0; i--) {
      const b = this._brood[i];
      if (b.phase === 'egg' && elapsed >= b.phaseFlipAt) b.phase = 'larva';
      if (elapsed >= b.hatchAt) {
        if (b.hungry && nurseAmbient === 0 && Math.random() < 0.5) {
          this._brood.splice(i, 1);
          continue;
        }
        const ang = Math.random() * Math.PI * 2;
        const r = Math.random() * 0.4;
        const x = this.nest.x + Math.cos(ang) * r;
        const z = this.nest.z + Math.sin(ang) * r;
        world.spawn(x, z, this.team, b.caste);
        this._brood.splice(i, 1);
      }
    }

    if (!this.alive) return;
    if (this.queenId < 0) return;
    const queen = world.ants[this.queenId];
    if (!queen || !queen.alive) {
      // Succession: if any nurse is alive, promote her to queen before
      // declaring the colony dead. Her larva-chewing mandibles already made
      // her a reasonable candidate for the role in this world.
      const promoted = this.promoteNurseToQueen(world);
      if (!promoted) {
        this.onQueenDeath();
        return;
      }
    }
    if (elapsed < this.nextEggAt) return;
    if (aliveTotal + this._brood.length >= this.maxPopulation) return;

    // Egg cost. If the stockpile can pay, deduct food. Otherwise the
    // queen burns 0.5 HP per egg — production keeps flowing in famine,
    // but the queen will exhaust herself in ~40 eggs without nurses /
    // food, which is the natural fail-safe the user asked for.
    const hungry = this.stockpile < this.eggCost;
    if (hungry) {
      const queenAnt = world.ants[this.queenId];
      if (queenAnt && queenAnt.alive) {
        queenAnt.hp -= 0.5;
        // Queen dies when she runs out — leave the kill to the global
        // hp <= 0 reapers + onQueenDeath succession path.
        if (queenAnt.hp <= 0) queenAnt.hp = 0;
      }
    } else {
      this.stockpile -= this.eggCost;
    }
    // Nurses tend brood — more nurses means a faster laying cadence so the
    // player's "more nurses → more eggs/larvae" intuition actually works.
    // Each nurse shaves a little off the next interval, capped at 5×.
    const nurseBoost = Math.min(5, 1 + nurseAmbient * 0.15);
    this.nextEggAt = elapsed + (this.eggInterval + Math.random() * 0.8) / nurseBoost;

    // Pick the caste the colony needs most. "Deficit" = target share minus
    // current share; highest deficit wins, ties broken by random for variety.
    const caste = this.pickNeededCaste(world.ants);
    this._brood.push({
      hatchAt: elapsed + this.hatchDuration,
      phaseFlipAt: elapsed + this.hatchDuration * 0.4,
      caste,
      phase: 'egg',
      hungry,
    });
  }

  snapshot(ants: readonly AntState[]): ColonySnapshot {
    let eggs = 0, larvae = 0;
    for (const b of this._brood) {
      if (b.phase === 'egg') eggs++; else larvae++;
    }
    const pop = this.countPerCaste(ants);
    return {
      team: this.team,
      alive: this.alive,
      stockpile: this.stockpile,
      eggs,
      larvae,
      population: pop,
      hasQueen: this.queenId >= 0 && !!ants[this.queenId] && ants[this.queenId].alive,
      ratios: { ...this.ratios },
    };
  }

  /** Find an alive same-team nurse, flip her caste to queen, and register her
   *  id as the colony's new queen. Returns true on success. Called from the
   *  update-loop the moment the old queen dies. */
  private promoteNurseToQueen(world: AntSimWorld): boolean {
    for (const a of world.ants) {
      if (a.alive && a.team === this.team && a.casteType === 'nurse') {
        // The sim's AntState is declared readonly-ish at the interface level,
        // but the concrete AntInternal object mutates freely — this write
        // reaches through to it and the sim reads the updated caste next tick.
        (a as AntState & { casteType: AntCaste }).casteType = 'queen';
        this.queenId = a.id;
        return true;
      }
    }
    return false;
  }

  private pickNeededCaste(ants: readonly AntState[]): AntCaste {
    const pop = this.countPerCaste(ants);
    const total = pop.worker + pop.soldier + pop.nurse + pop.scout + pop.breeder || 1;
    const castes: Array<keyof CasteRatios> = ['worker', 'soldier', 'nurse', 'scout', 'breeder'];
    // Normalize target ratios so user's slider sum doesn't matter.
    let targetSum = 0;
    for (const c of castes) targetSum += this.ratios[c];
    if (targetSum <= 0) targetSum = 1;

    let best: keyof CasteRatios = 'worker';
    let bestDeficit = -Infinity;
    for (const c of castes) {
      const target = this.ratios[c] / targetSum;
      const current = pop[c] / total;
      const deficit = target - current;
      if (deficit > bestDeficit + 1e-6 || (Math.abs(deficit - bestDeficit) < 1e-6 && Math.random() < 0.5)) {
        bestDeficit = deficit;
        best = c;
      }
    }
    return best;
  }

  private countPerCaste(ants: readonly AntState[]): Record<AntCaste, number> {
    const out: Record<AntCaste, number> = {
      worker: 0, soldier: 0, queen: 0, nurse: 0, scout: 0, breeder: 0,
    };
    for (const a of ants) {
      if (!a.alive || a.team !== this.team) continue;
      out[a.casteType]++;
    }
    return out;
  }
}
